import cannectLogo from '@cannect/assets/img/categoryCard/cannect.png'
import bannerAutocuidado from '@cannect/assets/img/categoryCard/fevBanner.png'
import { Skeleton, Typography } from '@cannect/new-components/atoms'
import { Button } from '@cannect/new-components/atoms/Button'
import api from '@cannect/services/api'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

type TBannerItem = {
  image_url?: string | null
  title?: string | null
  subtitle?: string | null
  description?: string | null
  button_text?: string | null
  cta_text?: string | null
  cta_link?: string | null
  coupon_code?: string | null
  link?: string | null
  url?: string | null
}

type TBanner = {
  id: number
  items?: TBannerItem[] | null
}

type TPromoBannerData = {
  imageUrl: string
  title: string
  subtitle: string
  description: string
  ctaText: string
  ctaLink?: string | null
  ctaCopyValue?: string | null
}

const DEFAULT_BANNER: TPromoBannerData = {
  imageUrl: bannerAutocuidado,
  title: 'Primeira compra com condição especial.',
  subtitle: 'Custo de importação de R$ 145 por R$ 100 em compras acima de R$ 499.',
  description: 'Pra sua saúde, o ano já começou.',
  ctaText: '1COMPRAFEV',
  ctaLink: null,
  ctaCopyValue: '1COMPRAFEV'
}

export const PromoBanner = () => {
  const [remoteBanner, setRemoteBanner] = useState<TPromoBannerData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    const fetchBanner = async () => {
      try {
        const { data } = await api.get<{ success: boolean; banners?: TBanner[] }>('/banners/active?segment=marketplace')
        const banners = data?.banners || []
        const selected = banners.find((banner) => banner.id === 2) || banners[1] || banners[0] || null
        const item = selected?.items?.[0] || null
        if (!item || !selected?.id) return

        const ctaText =
          item?.button_text || item?.cta_text || item?.coupon_code || DEFAULT_BANNER.ctaText || 'Saiba mais'
        const ctaLink = item?.cta_link || item?.link || item?.url || null
        const ctaCopyValue = item?.coupon_code || item?.cta_link || item?.cta_text || DEFAULT_BANNER.ctaCopyValue

        const payload: TPromoBannerData = {
          imageUrl: item?.image_url || DEFAULT_BANNER.imageUrl,
          title: item?.title || DEFAULT_BANNER.title,
          subtitle: item?.subtitle || DEFAULT_BANNER.subtitle,
          description: item?.description || DEFAULT_BANNER.description,
          ctaText,
          ctaLink,
          ctaCopyValue
        }

        if (isMounted) {
          setRemoteBanner(payload)
        }
      } catch (error) {
        // Mantem fallback local
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchBanner()

    return () => {
      isMounted = false
    }
  }, [])

  const banner = useMemo(() => remoteBanner || DEFAULT_BANNER, [remoteBanner])

  const isValidLink = (link?: string | null) => {
    if (!link) return false
    try {
      return /^https?:\/\//i.test(link)
    } catch {
      return false
    }
  }

  const handleCopyCoupon = async () => {
    const couponCode = banner.ctaCopyValue || banner.ctaText
    if (!couponCode) return

    try {
      await navigator.clipboard.writeText(couponCode)
      toast.success('Cupom copiado com sucesso!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
    } catch (err) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea')
      textArea.value = couponCode
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        toast.success('Cupom copiado com sucesso!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
      } catch (err) {
        toast.error('Erro ao copiar cupom. Tente novamente.')
      }
      document.body.removeChild(textArea)
    }
  }

  const handleCtaClick = () => {
    if (isValidLink(banner.ctaLink)) {
      window.open(banner.ctaLink as string, '_blank')
      return
    }
    handleCopyCoupon()
  }

  const renderHighlightCurrency = (text: string) => {
    if (!text) return text
    const regex = /(R\$\s*\d+(?:[.,]\d{2})?)/gi
    const parts = text.split(regex)
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={`${part}-${index}`} className="text-primary-800">
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  const renderSubtitle = (text: string) => {
    const token = 'em compras acima de'
    if (!text || !text.toLowerCase().includes(token)) {
      return renderHighlightCurrency(text)
    }
    const [before, after] = text.split(new RegExp(token, 'i'))
    const firstLine = before.trim()
    const secondLine = `${token}${after}`.trim()
    return (
      <>
        {renderHighlightCurrency(firstLine)}
        <br />
        {renderHighlightCurrency(secondLine)}
      </>
    )
  }

  if (isLoading) {
    return (
      <section className="w-full overflow-hidden rounded-md bg-primary-100">
        <div className="grid min-h-[160px] grid-cols-1 md:min-h-[140px] md:grid-cols-8">
          <div className="relative h-32 w-full md:col-span-3 md:h-full">
            <Skeleton className="h-full w-full" />
            <Skeleton className="absolute right-4 top-4 h-5 w-16 md:right-6 md:top-10 md:h-6" />
          </div>
          <div className="flex min-h-[128px] w-full flex-col gap-3 px-4 py-4 md:col-span-5 md:mb-2 md:grid md:min-h-[108px] md:grid-cols-[1fr_auto] md:items-center md:gap-4 md:px-4">
            <div className="ml-10 flex w-full flex-col gap-2">
              <Skeleton className="h-4 w-52" />
              <Skeleton className="h-4 w-72" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-7 w-28 rounded-lg" />
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="w-full overflow-hidden rounded-md bg-primary-100">
      <div className="grid min-h-[160px] grid-cols-1 md:min-h-[140px] md:grid-cols-8">
        {/* Imagem */}
        <div className="relative h-32 w-full md:col-span-3 md:h-full">
          <img src={banner.imageUrl} alt="Banner promocional" className="block h-full w-full object-cover" />

          <img
            src={cannectLogo}
            alt="Cannect"
            className="absolute right-4 top-4 h-5 w-auto md:right-6 md:top-10 md:h-6"
          />
        </div>

        <div className="flex min-h-[128px] w-full flex-col gap-3 px-4 py-4 md:col-span-5 md:mb-2 md:grid md:min-h-[108px] md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-4 md:px-4">
          <div className="flex w-full min-w-0 flex-col items-center gap-1 pr-4 text-center text-primary-900 md:ml-10 md:items-start md:text-left">
            <Typography.Text className="whitespace-normal break-words text-xs font-semibold leading-tight text-primary-900 sm:text-sm md:text-lg">
              {renderHighlightCurrency(banner.title)}
            </Typography.Text>
            <Typography.Text className="whitespace-normal break-words text-xs font-semibold leading-tight text-primary-900 sm:text-sm md:text-lg">
              {renderSubtitle(banner.subtitle)}
            </Typography.Text>
            <Typography.Text className="whitespace-normal break-words text-xs font-semibold leading-tight text-primary-900 sm:text-sm md:text-lg">
              {renderHighlightCurrency(banner.description)}
            </Typography.Text>
          </div>

          {/* Botão responsivo */}
          <Button
            size="xs"
            onClick={handleCtaClick}
            className="w-full rounded-lg bg-primary-800 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-primary-700 md:w-auto md:py-1">
            {banner.ctaText}
          </Button>
        </div>
      </div>
    </section>
  )
}
